import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Heart } from 'lucide-react';
import { toast } from 'sonner';

const WALLETS = [
  {
    name: 'Bitcoin',
    symbol: 'BTC',
    address: 'bc1qrqr997vdrk0m8klvahp5ncz53qf53rgtuls7gm',
    color: '#F7931A',
    icon: '₿',
    tokens: ['BTC'],
  },
  {
    name: 'Ethereum',
    symbol: 'ETH',
    address: '0x343a8748fd662accc9be692e7fc846eec50490cb',
    color: '#627EEA',
    icon: 'Ξ',
    tokens: ['ETH', 'USDC', 'USDT'],
  },
  {
    name: 'Litecoin',
    symbol: 'LTC',
    address: 'ltc1qhdwv9z7nwag2dtsrq2qxvgn36tqs67657nxp3g',
    color: '#BFBBBB',
    icon: 'Ł',
    tokens: ['LTC'],
  },
  {
    name: 'Solana',
    symbol: 'SOL',
    address: 'AjtfepAqSCXiQn1tCTy63nfmZrJQLvuhVizpS4jRvZSQ',
    color: '#9945FF',
    icon: '◎',
    tokens: ['SOL'],
  },
];

export default function Donation() {
  const [copied, setCopied] = useState('');

  const copyAddress = (address, symbol) => {
    navigator.clipboard.writeText(address);
    setCopied(symbol);
    toast.success(`${symbol} address copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.3em] text-rm-red mb-3">Support Us</p>
          <h1 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase tracking-tight mb-4" data-testid="donation-title">
            Support <span className="text-rm-red">RobloxMart</span>
          </h1>
          <p className="text-zinc-500 max-w-md mx-auto text-sm leading-relaxed">
            RobloxMart is a free platform. Your donations help keep the servers running and the platform free for everyone.
          </p>
        </div>

        {/* Wallets */}
        <div className="space-y-4">
          {WALLETS.map((wallet) => (
            <div key={wallet.symbol}
              className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm p-6 hover:border-[#2a2a2a] transition-all duration-200 relative overflow-hidden"
              data-testid={`wallet-${wallet.symbol.toLowerCase()}`}>
              {/* Subtle top accent */}
              <div className="absolute top-0 left-0 right-0 h-[1px]"
                style={{ background: `linear-gradient(90deg, transparent, ${wallet.color}40, transparent)` }} />

              <div className="flex flex-col md:flex-row items-center gap-6">
                {/* QR Code */}
                <div className="bg-white p-3 rounded-sm shrink-0 shadow-[0_0_20px_rgba(0,0,0,0.5)]">
                  <QRCodeSVG value={wallet.address} size={110} level="M" includeMargin={false} />
                </div>

                {/* Details */}
                <div className="flex-1 w-full min-w-0">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center font-bold text-lg shrink-0"
                      style={{ backgroundColor: wallet.color + '15', color: wallet.color, border: `1px solid ${wallet.color}30` }}>
                      {wallet.icon}
                    </div>
                    <div>
                      <h3 className="font-rajdhani font-bold text-xl uppercase tracking-wide" style={{ color: wallet.color }}>
                        {wallet.name}
                      </h3>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {wallet.tokens.map(t => (
                          <span key={t} className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm border"
                            style={{ color: wallet.color, borderColor: wallet.color + '30', backgroundColor: wallet.color + '08' }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="bg-black/40 border border-[#1e1e1e] rounded-sm p-3 flex items-center gap-2 group">
                    <code className="text-xs text-zinc-500 flex-1 break-all font-mono leading-relaxed group-hover:text-zinc-400 transition-colors"
                      data-testid={`wallet-address-${wallet.symbol.toLowerCase()}`}>
                      {wallet.address}
                    </code>
                    <button onClick={() => copyAddress(wallet.address, wallet.symbol)}
                      data-testid={`copy-${wallet.symbol.toLowerCase()}`}
                      className="shrink-0 w-8 h-8 flex items-center justify-center rounded-sm transition-all duration-200"
                      style={copied === wallet.symbol
                        ? { backgroundColor: wallet.color + '20', color: wallet.color }
                        : { color: '#4a4a4a' }}
                      title="Copy address">
                      {copied === wallet.symbol ? <Check size={13} /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center space-y-1">
          <p className="text-xs text-zinc-700">All donations are voluntary and non-refundable.</p>
          <p className="text-xs text-zinc-800">We do not track individual donations.</p>
        </div>
      </div>
    </div>
  );
}
